/**
 * Funciones para convertir props legacy a configuraciones del nuevo sistema
 */

import React from 'react';
import { InnerButtonProps } from '@/shared/types/button';
import {
  ButtonIconConfig,
  ButtonBadgeConfig,
  ButtonSlotConfig,
  ButtonTooltipConfig,
} from '@/shared/types/buttonConfig';
import { VisualSlotType, IconType, AnyIconDefinition } from '@/shared/types/types';
import { warnDeprecatedPropList } from './deprecation';

/**
 * Convierte las props legacy del icono principal a ButtonIconConfig
 * Solo se convierte si hay props de estilizado (duotone, etc.), de lo contrario
 * el icon simple funciona sin conversión
 */
export const convertLegacyIconToIconConfig = (
  props: Pick<
    InnerButtonProps,
    | 'icon'
    | 'iconDuotonePrimary'
    | 'iconDuotoneSecondary'
    | 'iconDuotoneOpacityPrimary'
    | 'iconDuotoneOpacitySecondary'
    | 'colorIcon'
    | 'classNameIcon'
    | 'onClickIcon'
  >
): ButtonIconConfig | undefined => {
  if (!props.icon) return undefined;

  // Si es un ReactElement, no puede convertirse a ButtonIconConfig
  if (React.isValidElement(props.icon)) {
    return undefined;
  }

  // Solo convertir si hay props de estilizado (duotone, colorIcon personalizado, etc.)
  // Si solo se usa icon sin estilos, no necesita conversión
  const hasStylingProps =
    props.iconDuotonePrimary ||
    props.iconDuotoneSecondary ||
    props.iconDuotoneOpacityPrimary !== undefined ||
    props.iconDuotoneOpacitySecondary !== undefined ||
    props.colorIcon ||
    props.classNameIcon ||
    props.onClickIcon;

  if (!hasStylingProps) {
    return undefined; // icon simple funciona sin conversión
  }

  const deprecatedUsed: string[] = [];
  if (props.iconDuotonePrimary) deprecatedUsed.push('iconDuotonePrimary');
  if (props.iconDuotoneSecondary) deprecatedUsed.push('iconDuotoneSecondary');
  if (props.iconDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('iconDuotoneOpacityPrimary');
  }
  if (props.iconDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('iconDuotoneOpacitySecondary');
  }
  if (props.colorIcon) deprecatedUsed.push('colorIcon');
  if (props.classNameIcon) deprecatedUsed.push('classNameIcon');
  if (props.onClickIcon) deprecatedUsed.push('onClickIcon');
  warnDeprecatedPropList(deprecatedUsed, 'iconProps');

  return {
    icon: props.icon as IconType | AnyIconDefinition,
    show: true,
    color: props.colorIcon,
    className: props.classNameIcon,
    onClick: props.onClickIcon,
    // Mapear iconDuotone* a colorDuotone* para retrocompatibilidad
    colorDuotonePrimary: props.iconDuotonePrimary,
    colorDuotoneSecondary: props.iconDuotoneSecondary,
    opacityDuotonePrimary: props.iconDuotoneOpacityPrimary,
    opacityDuotoneSecondary: props.iconDuotoneOpacitySecondary,
    // También mantener las props legacy para retrocompatibilidad
    iconDuotonePrimary: props.iconDuotonePrimary,
    iconDuotoneSecondary: props.iconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.iconDuotoneOpacitySecondary,
  };
};

/**
 * Convierte las props legacy del badge a ButtonBadgeConfig
 */
export const convertLegacyBadgeToBadgeConfig = (
  props: Pick<
    InnerButtonProps,
    | 'badgeColor'
    | 'badgeLabel'
    | 'badgeTheme'
    | 'badgeSize'
    | 'badgeStroke'
    | 'badgeIcon'
    | 'badgeIconDuotonePrimary'
    | 'badgeIconDuotoneSecondary'
    | 'badgeIconDuotoneOpacityPrimary'
    | 'badgeIconDuotoneOpacitySecondary'
    | 'badgeLoading'
  >
): ButtonBadgeConfig | undefined => {
  if (!props.badgeColor && !props.badgeLabel && !props.badgeIcon && !props.badgeLoading) {
    return undefined;
  }

  const deprecatedUsed: string[] = [];
  if (props.badgeColor) deprecatedUsed.push('badgeColor');
  if (props.badgeLabel) deprecatedUsed.push('badgeLabel');
  if (props.badgeIcon) deprecatedUsed.push('badgeIcon');
  if (props.badgeLoading) deprecatedUsed.push('badgeLoading');
  warnDeprecatedPropList(deprecatedUsed, 'badge');

  return {
    label: props.badgeLabel,
    color: props.badgeColor,
    theme: props.badgeTheme,
    size: props.badgeSize,
    stroke: props.badgeStroke,
    icon: props.badgeIcon,
    iconDuotonePrimary: props.badgeIconDuotonePrimary,
    iconDuotoneSecondary: props.badgeIconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.badgeIconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.badgeIconDuotoneOpacitySecondary,
    loading: props.badgeLoading,
    show: true,
  };
};

/**
 * Convierte las props legacy del slot izquierdo a ButtonSlotConfig
 * Solo se convierte si hay props de estilizado (duotone, onClick, etc.), de lo contrario
 * el leftSlot simple funciona sin conversión
 */
export const convertLegacyLeftSlotToSlotConfig = (
  props: Pick<
    InnerButtonProps,
    | 'leftSlot'
    | 'leftSlotDuotonePrimary'
    | 'leftSlotDuotoneSecondary'
    | 'leftSlotDuotoneOpacityPrimary'
    | 'leftSlotDuotoneOpacitySecondary'
    | 'onClickLeftSlot'
    | 'colorIcon'
  >
): ButtonSlotConfig | undefined => {
  if (!props.leftSlot) return undefined;

  // Solo convertir si hay props de estilizado (duotone, onClick, colorIcon personalizado)
  // Si solo se usa leftSlot sin estilos, no necesita conversión
  const hasStylingProps =
    props.leftSlotDuotonePrimary ||
    props.leftSlotDuotoneSecondary ||
    props.leftSlotDuotoneOpacityPrimary !== undefined ||
    props.leftSlotDuotoneOpacitySecondary !== undefined ||
    props.onClickLeftSlot ||
    props.colorIcon;

  if (!hasStylingProps) {
    return undefined; // leftSlot simple funciona sin conversión
  }

  const deprecatedUsed: string[] = [];
  if (props.leftSlotDuotonePrimary) deprecatedUsed.push('leftSlotDuotonePrimary');
  if (props.leftSlotDuotoneSecondary) deprecatedUsed.push('leftSlotDuotoneSecondary');
  if (props.leftSlotDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('leftSlotDuotoneOpacityPrimary');
  }
  if (props.leftSlotDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('leftSlotDuotoneOpacitySecondary');
  }
  if (props.onClickLeftSlot) deprecatedUsed.push('onClickLeftSlot');
  if (props.colorIcon) deprecatedUsed.push('colorIcon');
  warnDeprecatedPropList(deprecatedUsed, 'leftSlotProps');

  return {
    icon: props.leftSlot as VisualSlotType,
    colorIcon: props.colorIcon,
    onClick: props.onClickLeftSlot,
    iconDuotonePrimary: props.leftSlotDuotonePrimary,
    iconDuotoneSecondary: props.leftSlotDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.leftSlotDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.leftSlotDuotoneOpacitySecondary,
    show: true,
  };
};

/**
 * Convierte las props legacy del slot derecho a ButtonSlotConfig
 * Solo se convierte si hay props de estilizado (duotone, onClick, etc.), de lo contrario
 * el rightSlot simple funciona sin conversión
 */
export const convertLegacyRightSlotToSlotConfig = (
  props: Pick<
    InnerButtonProps,
    | 'rightSlot'
    | 'rightSlotDuotonePrimary'
    | 'rightSlotDuotoneSecondary'
    | 'rightSlotDuotoneOpacityPrimary'
    | 'rightSlotDuotoneOpacitySecondary'
    | 'onClickRightSlot'
    | 'colorIcon'
  >
): ButtonSlotConfig | undefined => {
  if (!props.rightSlot) return undefined;

  // Solo convertir si hay props de estilizado (duotone, onClick, colorIcon personalizado)
  // Si solo se usa rightSlot sin estilos, no necesita conversión
  const hasStylingProps =
    props.rightSlotDuotonePrimary ||
    props.rightSlotDuotoneSecondary ||
    props.rightSlotDuotoneOpacityPrimary !== undefined ||
    props.rightSlotDuotoneOpacitySecondary !== undefined ||
    props.onClickRightSlot ||
    props.colorIcon;

  if (!hasStylingProps) {
    return undefined; // rightSlot simple funciona sin conversión
  }

  const deprecatedUsed: string[] = [];
  if (props.rightSlotDuotonePrimary) deprecatedUsed.push('rightSlotDuotonePrimary');
  if (props.rightSlotDuotoneSecondary) deprecatedUsed.push('rightSlotDuotoneSecondary');
  if (props.rightSlotDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('rightSlotDuotoneOpacityPrimary');
  }
  if (props.rightSlotDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('rightSlotDuotoneOpacitySecondary');
  }
  if (props.onClickRightSlot) deprecatedUsed.push('onClickRightSlot');
  if (props.colorIcon) deprecatedUsed.push('colorIcon');
  warnDeprecatedPropList(deprecatedUsed, 'rightSlotProps');

  return {
    icon: props.rightSlot as VisualSlotType,
    colorIcon: props.colorIcon,
    onClick: props.onClickRightSlot,
    iconDuotonePrimary: props.rightSlotDuotonePrimary,
    iconDuotoneSecondary: props.rightSlotDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.rightSlotDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.rightSlotDuotoneOpacitySecondary,
    show: true,
  };
};

/**
 * Convierte las props legacy del tooltip a ButtonTooltipConfig
 * Solo se convierte si hay props de estilizado (position, alignment, delay, etc.), de lo contrario
 * el tooltip simple funciona sin conversión
 */
export const convertLegacyTooltipToTooltipConfig = (
  props: Pick<
    InnerButtonProps,
    | 'tooltip'
    | 'tooltipPosition'
    | 'tooltipAlignment'
    | 'tooltipDelay'
    | 'tooltipAbsolute'
    | 'classNameTooltip'
  >
): ButtonTooltipConfig | undefined => {
  if (!props.tooltip || typeof props.tooltip !== 'string') return undefined;

  // Solo convertir si hay props de estilizado (position, alignment, delay, etc.)
  // Si solo se usa tooltip sin estilos, no necesita conversión
  const hasStylingProps =
    props.tooltipPosition ||
    props.tooltipAlignment ||
    props.tooltipDelay !== undefined ||
    props.tooltipAbsolute !== undefined ||
    props.classNameTooltip;

  if (!hasStylingProps) {
    return undefined; // tooltip simple funciona sin conversión
  }

  const deprecatedUsed: string[] = [];
  if (props.tooltipPosition) deprecatedUsed.push('tooltipPosition');
  if (props.tooltipAlignment) deprecatedUsed.push('tooltipAlignment');
  if (props.tooltipDelay !== undefined) deprecatedUsed.push('tooltipDelay');
  if (props.tooltipAbsolute !== undefined) deprecatedUsed.push('tooltipAbsolute');
  if (props.classNameTooltip) deprecatedUsed.push('classNameTooltip');
  warnDeprecatedPropList(deprecatedUsed, 'tooltip como objeto o tooltipProps');

  return {
    label: props.tooltip,
    position: props.tooltipPosition,
    alignment: props.tooltipAlignment,
    delay: props.tooltipDelay,
    absolute: props.tooltipAbsolute,
    className: props.classNameTooltip,
    show: true,
  };
};
